import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';

import { setFiltersFromAlert, setView } from 'actions/search';
import { views } from 'components/Lists/constants';
import { searchOrigin } from 'components/Search/Results/constants';

const VIEW_TO_ORIGIN = {
  [views.researchers]: searchOrigin.researcherSearch,
  [views.organisations]: searchOrigin.organisationSearch,
};

// Accepts either the views-map KEY (plural, MCP convention: 'researchers') or
// VALUE (singular, SPA convention: 'researcher'). Normalises to the canonical
// singular value. Built on a null-prototype object to prevent prototype lookups
// (e.g. 'toString', '__proto__', 'constructor') returning inherited values.
const VIEW_NORMALISERS = Object.entries(views).reduce((acc, [key, value]) => {
  acc[key] = value; // plural key -> singular value
  acc[value] = value; // singular value -> singular value (passthrough)
  return acc;
}, Object.create(null));

export function normaliseView(viewParam) {
  return VIEW_NORMALISERS[viewParam] ?? null;
}

// Strict size limits to prevent DoS via massive payloads.
export const MAX_FILTER_BYTES = 8 * 1024; // 8 KB
export const MAX_FILTER_KEYS = 50; // top-level keys
export const MAX_DEPTH = 4; // nested object depth
export const MAX_STRING_LENGTH = 2048; // any single string value
export const MAX_ARRAY_LENGTH = 200; // any single array length

/**
 * Hydrates Redux from `?view=...&filter=...` query params, then strips them
 * from the URL so a refresh doesn't replay. Runs once on mount.
 *
 * Security posture:
 * - The URL is attacker-controllable (phishing). All inputs are strictly
 *   validated before any dispatch.
 * - view is whitelisted against the SPA's own enum.
 * - filter must parse as a plain object, fit within size/depth/key limits,
 *   contain only string / number / boolean / array-of-primitives / nested
 *   primitive-object leaves, and not exceed string-length caps.
 * - On any validation failure, no dispatch occurs; a console warning logs
 *   the failure mode.
 */
const HydrateFromUrl = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const params = new URLSearchParams(search);
    const viewParam = params.get('view');
    const filterParam = params.get('filter');
    if (!viewParam || !filterParam) return;

    // 1. View must be a known enum member — accept either the views-map KEY
    //    (plural, MCP convention: 'researchers') or VALUE (singular, SPA
    //    convention: 'researcher'). Normalise to the canonical singular value.
    const viewKey = normaliseView(viewParam);
    if (!viewKey) {
      console.warn('[HydrateFromUrl] Unknown view, ignoring:', viewParam);
      return;
    }
    const origin = VIEW_TO_ORIGIN[viewKey];
    if (!origin) {
      console.warn('[HydrateFromUrl] No origin for view:', viewKey);
      return;
    }

    // 2. Filter must fit the size cap before parsing.
    if (filterParam.length > MAX_FILTER_BYTES) {
      console.warn('[HydrateFromUrl] Filter exceeds size cap; ignoring.');
      return;
    }

    // 3. Parse JSON safely. JSON.parse is V8-safe; no eval; no prototype pollution
    //    via __proto__ keys (treated as plain props in modern V8).
    let parsed;
    try {
      parsed = JSON.parse(filterParam);
    } catch (err) {
      console.warn('[HydrateFromUrl] Filter is not valid JSON; ignoring.', err);
      return;
    }

    // 4. Shape validation.
    const validation = validateFilter(parsed);
    if (!validation.ok) {
      console.warn('[HydrateFromUrl] Filter rejected:', validation.reason);
      return;
    }

    // 5. Default categories to all-on if the URL didn't supply any. The Search
    //    page's useDefaultSearchCategories hook only resets categories when the
    //    set is empty, so an MCP-supplied filter without a `category` key would
    //    otherwise leave nothing selected (no results). Mirror the canonical
    //    industry-license default; the hook strips `profile` downstream for
    //    non-industry users.
    const filterToDispatch = withDefaultCategories(parsed);

    // 6. Dispatch + strip URL.
    dispatch(setFiltersFromAlert(origin, filterToDispatch));
    dispatch(setView(viewKey));
    history.replace(pathname);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

/**
 * Returns { ok: true } or { ok: false, reason: string }.
 * Allowed leaves: string (length-capped), number (finite), boolean, null.
 * Allowed containers: plain object (key count + depth-capped), array
 * (length-capped). No prototypes, no functions, no Symbols, no other types.
 */
export function validateFilter(value, depth = 0) {
  if (depth > MAX_DEPTH) return { ok: false, reason: 'depth exceeded' };

  const t = typeof value;
  if (value === null || t === 'boolean') return { ok: true };
  if (t === 'number') {
    if (!Number.isFinite(value))
      return { ok: false, reason: 'non-finite number' };
    return { ok: true };
  }
  if (t === 'string') {
    if (value.length > MAX_STRING_LENGTH)
      return { ok: false, reason: 'string too long' };
    return { ok: true };
  }
  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_LENGTH)
      return { ok: false, reason: 'array too long' };
    for (const item of value) {
      const r = validateFilter(item, depth + 1);
      if (!r.ok) return r;
    }
    return { ok: true };
  }
  if (t === 'object') {
    // Reject anything that's not a plain object literal.
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      return { ok: false, reason: 'non-plain object' };
    }
    const keys = Object.keys(value);
    if (depth === 0 && keys.length > MAX_FILTER_KEYS) {
      return { ok: false, reason: 'too many top-level keys' };
    }
    for (const k of keys) {
      if (typeof k !== 'string') return { ok: false, reason: 'non-string key' };
      // Reject __proto__ / constructor / prototype as keys defensively.
      if (k === '__proto__' || k === 'constructor' || k === 'prototype') {
        return { ok: false, reason: 'forbidden key' };
      }
      const r = validateFilter(value[k], depth + 1);
      if (!r.ok) return r;
    }
    return { ok: true };
  }
  return { ok: false, reason: `unsupported type: ${t}` };
}

/**
 * Canonical category-flag default used when the hydrated filter doesn't carry
 * any active category. Mirrors useDefaultSearchCategories' industry-license
 * shape; the hook strips `profile` downstream for non-industry users.
 */
export const DEFAULT_ALL_CATEGORIES = Object.freeze({
  funding: true,
  tradeshows: true,
  publications: true,
  clinicalTrials: true,
  profile: true,
});

/**
 * Returns a copy of `parsed` with `category` defaulted to all-on when the
 * input has no category object, an empty object, or no truthy entries.
 */
export function withDefaultCategories(parsed) {
  const hasCategory =
    parsed &&
    typeof parsed.category === 'object' &&
    parsed.category !== null &&
    Object.values(parsed.category).some(v => v === true);
  if (hasCategory) return parsed;
  return { ...parsed, category: { ...DEFAULT_ALL_CATEGORIES } };
}

export default HydrateFromUrl;
